import { useState, type ChangeEvent } from "react";
import { useMarket } from "@ferrivia/i18n";
import { DATE_PATTERNS, dateTextToIso, isoToDateText, maskDateText } from "./dateText";

/**
 * Props for a typed date field (spread onto TextInput or Field) whose order
 * follows the market, not the device. A native `<input type="date">` renders
 * in the browser's own region, so two people in the same market saw different
 * formats.
 *
 * `value`/`onChange` speak ISO "YYYY-MM-DD", and "" while the text is
 * incomplete or not a real date — the same contract the native input had.
 */
export function useDateInput(value: string, onChange: (iso: string) => void) {
  const market = useMarket();
  const [text, setText] = useState(() => isoToDateText(value, market));
  const [syncedValue, setSyncedValue] = useState(value);

  if (value !== syncedValue) {
    setSyncedValue(value);
    if (value !== dateTextToIso(text, market)) setText(isoToDateText(value, market));
  }

  const complete = text.replace(/\D/g, "").length === 8;

  return {
    type: "text",
    inputMode: "numeric",
    autoComplete: "off",
    placeholder: DATE_PATTERNS[market].placeholder,
    value: text,
    invalid: complete && dateTextToIso(text, market) === "",
    onChange: (event: ChangeEvent<HTMLInputElement>) => {
      const next = maskDateText(event.target.value, market);
      const iso = dateTextToIso(next, market);
      setText(next);
      setSyncedValue(iso);
      if (iso !== value) onChange(iso);
    },
  } as const;
}
